import React, { useEffect, useRef, useState } from 'react';
import { ChatMessage, IExternalChatDecision, IFormApplicationDTO } from 'shared/interfaces/formApplication';
import './ChatContainer.scss';
import { chatMessagesService } from 'api/chatMessages.service';
import { formatRelativeDate } from 'shared';
import { getAvailableChats, filterMessagesForChat, sortChatParticipants, ChatParticipant } from 'Utils/chatUtils';
import { InternalUser } from 'shared/interfaces/form';
import { useNotification } from 'hooks/useNotification';
import { NotificationSnackbar } from 'components/NotificationSnackbar';

/** The approver an admin decided for — a group reads `קבוצת "X"` (as before #143). */
const adminReplacedApprover = (decision: IExternalChatDecision): string => {
    if (!decision.approverTitle) return 'המאשר';
    return decision.approverType === 'Group' ? `קבוצת "${decision.approverTitle}"` : decision.approverTitle;
};

interface ChatContainerProps {
    application: IFormApplicationDTO;
    applicationId: string;
    messages: ChatMessage[];
    currentUser: string;
    userManager?: InternalUser | null;
    onMessagesUpdate?: (messages: ChatMessage[]) => void;
}

export const ChatContainer: React.FC<ChatContainerProps> = ({
    application,
    messages,
    currentUser,
    applicationId,
    userManager,
    onMessagesUpdate,
}) => {
    const { notification, showNotification, hideNotification } = useNotification();
    const [currentMessages, setCurrentMessages] = useState<ChatMessage[]>(messages || []);
    const [newMessage, setNewMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [justAddedId, setJustAddedId] = useState<string | null>(null);
    const [selectedChat, setSelectedChat] = useState<'public' | string>('public');
    const [availableChats, setAvailableChats] = useState<ChatParticipant[]>([]);
    const messagesEndRef = useRef<HTMLDivElement | null>(null);

    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollTop = messagesEndRef.current?.scrollHeight;
        }
    };

    useEffect(() => {
        // Initialize available chats
        const chats = getAvailableChats(application, currentUser, userManager);
        const sortedChats = sortChatParticipants(chats, messages, currentUser);
        setAvailableChats(sortedChats);
    }, [application, currentUser, userManager, messages]);

    useEffect(() => {
        // Filter messages based on selected chat
        const filteredMessages = filterMessagesForChat(messages, currentUser, selectedChat);
        setCurrentMessages(filteredMessages);
    }, [messages, currentUser, selectedChat]);

    const handleSend = async () => {
        if (!newMessage.trim()) return;
        setSending(true);
        try {
            const newMassagesArray: ChatMessage[] = await chatMessagesService.add(applicationId, {
                author: currentUser,
                content: newMessage,
                isDecisionRelated: false,
                messageTimestamp: new Date(),
                public: selectedChat === 'public',
                to: selectedChat === 'public' ? '' : selectedChat,
            });

            // Update parent component with all messages
            if (onMessagesUpdate) {
                onMessagesUpdate(newMassagesArray);
            }

            // Filter the returned messages based on the current chat selection
            const filteredMessages = filterMessagesForChat(newMassagesArray, currentUser, selectedChat);
            setCurrentMessages(filteredMessages);
            setNewMessage('');

            // add fade-in effect for the last message in the filtered list
            if (filteredMessages.length > 0) {
                setJustAddedId(filteredMessages[filteredMessages.length - 1].id);
                setTimeout(() => setJustAddedId(null), 1200); // אחרי 1.2 שניות מסיר את האפקט
            }
            // scroll to bottom after new message is added
            setTimeout(scrollToBottom, 500);
        } catch (e) {
            showNotification('שליחת ההודעה נכשלה', 'error');
        }
        setSending(false);
    };

    return (
        <>
            {/* Chat Selection Dropdown */}
            <div className="chat-selector">
                <select value={selectedChat} onChange={(e) => setSelectedChat(e.target.value)} className="chat-dropdown">
                    <option value="public" className="chat-option-public">
                        כולם (צ'אט כללי)
                    </option>
                    {availableChats.map((participant) => (
                        <option
                            key={participant.name}
                            value={participant.name}
                            className={`chat-option ${
                                participant.isApplicant
                                    ? 'chat-option-applicant'
                                    : participant.isManager
                                    ? 'chat-option-manager'
                                    : ''
                            }`}
                        >
                            {participant.name}
                            {participant.isApplicant && ' (מגיש הבקשה)'}
                            {participant.isManager && ' (מנהל)'}
                        </option>
                    ))}
                </select>
            </div>

            <div className="chat-messages-list" ref={messagesEndRef}>
                {currentMessages.length === 0 ? (
                    <div className="chat-empty-message">
                        <span role="img" aria-label="chat">
                            💬
                        </span>
                        <div>
                            {selectedChat === 'public'
                                ? "אין עדיין הודעות בצ'אט הכללי"
                                : `אין עדיין הודעות בצ'אט עם ${selectedChat}`}
                        </div>
                    </div>
                ) : (
                    currentMessages.map((msg) => {
                        const isMine = msg.author === currentUser;

                        // Determine CSS class for decision/system action messages
                        let actionClass = '';
                        if (msg.isDecisionRelated) {
                            if (msg.cancellationStatus === 'cancelled') {
                                actionClass = 'cancelled';
                            } else if (msg.cancellationStatus === 'restored') {
                                actionClass = 'restored';
                            } else if (msg.decision?.decisionStatus === 'approved') {
                                actionClass = 'approved';
                            } else if (msg.decision?.decisionStatus === 'denied') {
                                actionClass = 'rejected';
                            }
                        }

                        return (
                            <div
                                key={msg.id}
                                className={`chat-message ${isMine ? 'mine' : 'theirs'} ${actionClass} ${
                                    msg.id === justAddedId ? 'fade-in' : ''
                                }`.trim()}
                            >
                                <div className="chat-author">
                                    <span>{msg.isDecisionRelated ? 'מערכת' : msg.author}</span>
                                    <span>{formatRelativeDate(msg.messageTimestamp)}</span>
                                </div>
                                {msg.isDecisionRelated && (
                                    <div className="chat-decision-title">
                                        <b>
                                            {msg.cancellationStatus === 'cancelled' ? (
                                                <>
                                                    ההגשה בוטלה על ידי {msg.author}
                                                    {msg.content ? ', עם ההערה הבאה:' : '.'}
                                                </>
                                            ) : msg.cancellationStatus === 'restored' ? (
                                                <>
                                                    ההגשה שוחזרה על ידי {msg.author}
                                                    {msg.content ? ', עם ההערה הבאה:' : '.'}
                                                </>
                                            ) : (
                                                <>
                                                    שלב {msg.decision?.decisionStatus === 'approved' ? 'אושר' : 'נדחה'} בסבב
                                                    האישורים, על ידי{' '}
                                                    {msg.decision?.adminDecisionUserName || msg.author}
                                                    {msg.decision?.adminDecisionUserName
                                                        ? `, שאישר את השלב כאדמין במקום ${adminReplacedApprover(msg.decision)}`
                                                        : ''}
                                                    {msg.content ? ', עם ההערה הבאה:' : ''}
                                                </>
                                            )}
                                        </b>
                                    </div>
                                )}
                                <div className="chat-content">{msg.content}</div>
                            </div>
                        );
                    })
                )}
            </div>

            <div className="chat-input-row">
                <input
                    type="text"
                    placeholder={selectedChat === 'public' ? 'הקלד הודעה לכולם...' : `הקלד הודעה ל${selectedChat}...`}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                    disabled={sending}
                />
                <button
                    onClick={handleSend}
                    disabled={
                        sending ||
                        !newMessage.trim() ||
                        application.applicationStatus === 'approved' ||
                        application.applicationStatus === 'cancelled'
                    }
                >
                    שלח
                </button>
            </div>

            <NotificationSnackbar
                open={notification.open}
                message={notification.message}
                severity={notification.severity}
                onClose={hideNotification}
            />
        </>
    );
};
